import React, { createContext, useContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

const AuthContext = createContext({});

export const useAuth = () => {
  const context = useContext(AuthContext);
  if (!context) {
    throw new Error('useAuth must be used within AuthProvider');
  }
  return context;
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [userProfile, setUserProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    // Get initial session with error handling for invalid refresh tokens
    supabase?.auth?.getSession()?.then(({ data: { session }, error }) => {
      if (error) {
        console.error('Session error:', error?.message);
        // Clear invalid session if refresh token error
        if (error?.message?.includes('refresh_token_not_found') || error?.message?.includes('Invalid Refresh Token')) {
          supabase?.auth?.signOut();
        }
        setUser(null);
        setLoading(false);
        return;
      }
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
      } else {
        setLoading(false);
      }
    })?.catch((err) => {
      console.error('Failed to get session:', err);
      setUser(null);
      setLoading(false);
    });

    // Listen for auth changes
    const { data: { subscription } } = supabase?.auth?.onAuthStateChange((event, session) => {
      // Handle token refresh errors
      if (event === 'TOKEN_REFRESHED') {
        console.log('Token refreshed successfully');
      }
      
      if (event === 'SIGNED_OUT') {
        setUser(null);
        setUserProfile(null);
        setLoading(false);
      }
      
      setUser(session?.user ?? null);
      if (session?.user) {
        loadUserProfile(session?.user?.id);
      } else {
        setUserProfile(null);
        setLoading(false);
      }
    });

    return () => subscription?.unsubscribe();
  }, []);

  const loadUserProfile = async (userId) => {
    try {
      const { data, error } = await supabase?.from('user_profiles')?.select('*')?.eq('id', userId)?.single();

      if (error) throw error;
      setUserProfile(data);
    } catch (error) {
      console.error('Error loading user profile:', error?.message);
    } finally {
      setLoading(false);
    }
  };

  const signIn = async (email, password) => {
    const { data, error } = await supabase?.auth?.signInWithPassword({
      email,
      password,
    });
    if (error) throw error;
    return data;
  };

  const signUp = async (email, password, metadata = {}) => {
    const { data, error } = await supabase?.auth?.signUp({
      email,
      password,
      options: {
        data: {
          full_name: metadata?.full_name || '',
          phone: metadata?.phone || '',
          role: metadata?.role || 'agent',
        },
      },
    });
    if (error) throw error;
    
    // Send admin notification for new user registration
    if (data?.user) {
      try {
        const userApprovalService = (await import('../services/userApprovalService'))?.default;
        await userApprovalService?.sendAdminNotification(
          data?.user?.email,
          metadata?.full_name || data?.user?.email,
          metadata?.role || 'agent',
          data?.user?.id
        );
      } catch (notificationError) {
        console.error('Failed to send admin notification:', notificationError);
        // Don't throw - user registration succeeded even if notification failed
      }
    }
    
    return data;
  };

  const signOut = async () => {
    try {
      const { error } = await supabase?.auth?.signOut();
      if (error) throw error;
      // Clear any remaining session data
      setUser(null);
      setUserProfile(null);
    } catch (error) {
      console.error('Sign out error:', error?.message);
      // Force clear local state even if signOut fails
      setUser(null);
      setUserProfile(null);
      // Clear localStorage manually if needed
      localStorage.removeItem('supabase.auth.token');
    }
  };

  const value = {
    user,
    userProfile,
    loading,
    signIn,
    signUp,
    signOut,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export default AuthContext;
